import { useCallback, useEffect, useState } from 'react';
import I18n from 'I18n';
import { EMBED_VERSIONS } from 'ContentUtils/constants/FormsCRM';
import Routes from 'ContentUtils/Routes';
import { Select } from 'foundations-components/transitional/components/Select';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Link } from 'foundations-components/transitional/components/Link';
import { Badge } from 'foundations-components/transitional/components/Badge';
import useForms from './utils/useForms';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = suffix => `ContentComponents.cmv2.fields.baseResource.${suffix}`;
const formI18nKey = suffix => `ContentComponents.cmv2.fields.form.${suffix}`;
const getResultsAsOptions = results => results.map(({
  properties: {
    hs_form_id: {
      value
    },
    hs_name: {
      value: text
    },
    hs_embed_version: {
      value: embedVersion = undefined
    } = {}
  }
}) => Object.assign({
  value,
  text
}, embedVersion === EMBED_VERSIONS.V4 && {
  badge: /*#__PURE__*/_jsx(Badge, {
    use: "new",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: formI18nKey('badgeNew')
    })
  })
}));
const Footer = () => /*#__PURE__*/_jsx(Link, {
  external: true,
  href: Routes.newForm(),
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: formI18nKey('withInlineEditing.createForm.label')
  })
});
const SimplifiedFormSelect = ({
  value,
  embedVersions,
  requiredPropertyTypes,
  readOnly,
  useOnDark,
  onChange
}) => {
  const [options, setOptions] = useState([]);
  const [formFetchSucceeded, setFormFetchSucceeded] = useState(false);
  const [formFetchFailed, setFormFetchFailed] = useState(false);
  const {
    getAllDebounced,
    getByFormId
  } = useForms();
  const isLoadingSelectedForm = Boolean(value && !formFetchSucceeded && !formFetchFailed);
  const selectedFormNotFound = Boolean(value && formFetchFailed);
  const handleOnChange = useCallback(selectedResult => {
    var _selectedResult$prope, _selectedResult$prope2;
    if (!onChange) return;
    onChange({
      formId: (_selectedResult$prope = selectedResult.properties.hs_form_id) === null || _selectedResult$prope === void 0 ? void 0 : _selectedResult$prope.value,
      embedVersion: ((_selectedResult$prope2 = selectedResult.properties.hs_embed_version) === null || _selectedResult$prope2 === void 0 ? void 0 : _selectedResult$prope2.value) || EMBED_VERSIONS.V2
    });
  }, [onChange]);
  useEffect(() => {
    if (value) {
      getByFormId(value).then(result => {
        setOptions(getResultsAsOptions([result]));
        setFormFetchSucceeded(true);
        handleOnChange(result);
      }).catch(() => {
        setFormFetchFailed(true);
        setOptions([]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return isLoadingSelectedForm ? /*#__PURE__*/_jsx(LoadingSpinner, {
    "data-test-id": "form:simplifiedFormSelect:loading",
    size: "extra-small",
    layout: "centered",
    grow: true
  }) : /*#__PURE__*/_jsx(Select, {
    "data-test-id": "form:simplifiedFormSelect",
    "data-selenium": "form:formSelect",
    error: selectedFormNotFound,
    menuWidth: "auto",
    value: value,
    readOnly: readOnly,
    buttonUse: useOnDark ? 'form-on-dark' : 'form',
    options: options,
    dropdownFooter: /*#__PURE__*/_jsx(Footer, {}),
    placeholder: I18n.text(i18nKey('placeholder'), {
      type: I18n.text(i18nKey('typeLabels.form'), {
        count: 1
      })
    }),
    loadOptions: (query = '', cb, {
      offset = 0
    } = {}) => {
      getAllDebounced({
        query,
        offset,
        embedVersions,
        requiredPropertyTypes
      }, result => {
        const formsAsOptions = getResultsAsOptions(result.results);
        cb(null, {
          options: formsAsOptions,
          pagination: {
            hasMore: result.hasMore,
            offset: result.count,
            length: result.total
          }
        });
      });
    },
    onChange: e => {
      setFormFetchSucceeded(false);
      setFormFetchFailed(false);
      if (onChange) {
        getByFormId(e.target.value).then(result => {
          setOptions(getResultsAsOptions([result]));
          setFormFetchSucceeded(true);
          handleOnChange(result);
        }).catch(() => {
          setFormFetchFailed(true);
        });
      }
    }
  });
};
export default SimplifiedFormSelect;